import { applySnapshot, types } from 'mobx-state-tree';
import { endOfISOWeek, format, parse, startOfDay, startOfISOWeek } from 'date-fns';
import { t } from 'i18next';
import showNotification from 'components/extras/showNotification';
import CompanyService from 'services/CompanyService';
import { isOvertimeDetailSelectable } from 'pages/CommonMenu/Member/CompensationLeave/helpers';
import { rootStore } from '.';
import { getLocalizedField } from 'helpers/localizedField';

const TAB_TARGET = 'target';
const TAB_OVERTIME_DETAIL = 'overtimeDetail';
const SECOND_PER_HOUR = 3600;
const DEFAULT_PREMIUM_RATE = {
    overtime: 0.5,
    night: 0.5,
    holiday: 0.5,
};

const formatLocalDate = (date) => format(date, 'yyyy-MM-dd');
const parseLocalDate = (value) => parse(value, 'yyyy-MM-dd', new Date());
const getDefaultWeekStart = () => formatLocalDate(startOfISOWeek(new Date()));
const getDefaultWeekEnd = () => formatLocalDate(startOfDay(endOfISOWeek(new Date())));

/** Same formula as CompanySatoryLeaveBodyManagement */
const calculateCompensatoryHours = (item, premiumRate = {}, isUseAllCpLeaveAccrualScope = false) => {
    const overtimeRate = premiumRate.overtime ?? DEFAULT_PREMIUM_RATE.overtime;
    const nightRate = premiumRate.night ?? DEFAULT_PREMIUM_RATE.night;
    const holidayRate = premiumRate.holiday ?? DEFAULT_PREMIUM_RATE.holiday;

    const overtime = item?.overtime ?? 0;
    const nightWork = item?.nightWork ?? 0;
    const nightOvertime = item?.nightOvertime ?? 0;
    const holidayWork = item?.holidayWork ?? 0;
    const holidayOvertime = item?.holidayOvertime ?? 0;
    const holidayNightWork = item?.holidayNightWork ?? 0;
    const holidayNightOvertime = item?.holidayNightOvertime ?? 0;

    const baseSeconds = isUseAllCpLeaveAccrualScope ? (
        overtime +
        nightWork +
        nightOvertime +
        holidayWork +
        holidayOvertime +
        holidayNightWork +
        holidayNightOvertime) : 0;

    const premiumSeconds =
        overtime * overtimeRate +
        nightWork * nightRate +
        nightOvertime * (nightRate + overtimeRate) +
        holidayWork * holidayRate +
        holidayOvertime * (holidayRate + overtimeRate) +
        holidayNightWork * (holidayRate + nightRate) +
        holidayNightOvertime * (holidayRate + nightRate + overtimeRate);

    return (baseSeconds + premiumSeconds) / SECOND_PER_HOUR;
};

export const CompensationLeave = types
    .model('CompensationLeave', {
        filterParams: types.optional(
            types.model('CompensationLeaveFilterParams', {
                startDate: types.optional(types.string, getDefaultWeekStart()),
                endDate: types.optional(types.string, getDefaultWeekEnd()),
                name: types.optional(types.string, ''),
                hireType: types.optional(types.string, ''),
                area: types.optional(types.string, ''),
                tab: types.optional(types.string, TAB_TARGET),
            }),
            {},
        ),
        hireTypeList: types.optional(types.array(types.frozen()), []),
        workPlaceList: types.optional(types.array(types.frozen()), []),
        targetData: types.optional(types.array(types.frozen()), []),
        overtimeDetailData: types.optional(types.array(types.frozen()), []),
        selectedDetailIds: types.optional(types.array(types.number), []),
        filterUserId: types.maybeNull(types.number),
        dataCount: types.optional(types.number, 0),
        isLoading: types.optional(types.boolean, false),
        isBackgroundLoading: types.optional(types.boolean, false),
        /** Page size sent as API `limit` */
        size: types.optional(types.number, 500),
        /** Bumped to cancel an in-progress background fetch */
        backgroundFetchRunId: types.optional(types.number, 0),
        /** Bumped on filter reset / tab change so MonthNavigator resets calendar UI */
        calendarResetKey: types.optional(types.number, 0),
    })
    .views((self) => ({
        get dateRange() {
            return {
                startDate: parseLocalDate(self.filterParams.startDate),
                endDate: parseLocalDate(self.filterParams.endDate),
            };
        },
        get isOvertimeDetailTab() {
            return self.filterParams.tab === TAB_OVERTIME_DETAIL;
        },
        get currentListData() {
            return self.isOvertimeDetailTab ? self.overtimeDetailData : self.targetData;
        },
        get selectableDetailIndexes() {
            return self.overtimeDetailData.reduce((indexes, row, rowIndex) => {
                if (isOvertimeDetailSelectable(row)) {
                    indexes.push(rowIndex);
                }
                return indexes;
            }, []);
        },
        get areAllSelectableDetailsSelected() {
            const selectable = self.selectableDetailIndexes;
            if (!selectable.length) {
                return false;
            }
            return selectable.every((rowIndex) => self.selectedDetailIds.includes(rowIndex));
        },
    }))
    .actions((self) => ({
        reset() {
            applySnapshot(self, {});
        },

        setFilterParams(params) {
            const currentFilters = self.filterParams;
            Object.keys(params).forEach((key) => {
                if (key in currentFilters) {
                    currentFilters[key] = params[key];
                }
            });
        },

        resetFilters() {
            self.cancelBackgroundLoading();
            const f = self.filterParams;
            f.startDate = getDefaultWeekStart();
            f.endDate = getDefaultWeekEnd();
            f.name = '';
            f.hireType = '';
            f.area = '';
            self.setFilterUserId(null);
            self.calendarResetKey += 1;
        },

        setTab(tab, { resetFilters: shouldResetFilters = true } = {}) {
            self.cancelBackgroundLoading();
            self.filterParams.tab = tab;
            if (shouldResetFilters) {
                self.resetFilters();
            }
            self.clearSelectedDetailIds();
        },

        setFilterUserId(userId) {
            self.filterUserId = userId ?? null;
        },

        setDateRange(startDate, endDate) {
            // date-fns format 은 로컬 캘린더 일 기준이라 UTC 로 하루 밀리지 않음
            self.filterParams.startDate = formatLocalDate(startDate);
            self.filterParams.endDate = formatLocalDate(endDate);
        },

        setHireTypeList(list) {
            self.hireTypeList = list;
        },

        setWorkPlaceList(list) {
            self.workPlaceList = list;
        },

        setTargetData(data) {
            self.targetData.replace(data || []);
        },

        appendTargetData(data) {
            if (data?.length) {
                self.targetData.push(...data);
            }
        },

        setOvertimeDetailData(data) {
            self.overtimeDetailData.replace(data || []);
        },

        appendOvertimeDetailData(data) {
            if (data?.length) {
                self.overtimeDetailData.push(...data);
            }
        },

        setDataCount(count) {
            self.dataCount = count;
        },

        setIsLoading(loading) {
            self.isLoading = loading;
        },

        setIsBackgroundLoading(loading) {
            self.isBackgroundLoading = loading;
        },

        cancelBackgroundLoading() {
            self.backgroundFetchRunId += 1;
            self.isBackgroundLoading = false;
        },

        clearSelectedDetailIds() {
            self.selectedDetailIds.clear();
        },

        toggleDetailSelection(rowIndex) {
            const row = self.overtimeDetailData[rowIndex];
            if (!isOvertimeDetailSelectable(row)) {
                return;
            }

            const index = self.selectedDetailIds.indexOf(rowIndex);
            if (index > -1) {
                self.selectedDetailIds.splice(index, 1);
            } else {
                self.selectedDetailIds.push(rowIndex);
            }
        },

        toggleSelectAllDetails(checked) {
            if (checked) {
                self.selectedDetailIds.replace(self.selectableDetailIndexes.slice());
            } else {
                self.selectedDetailIds.clear();
            }
        },

        getSelectedAttendIds() {
            const attendIds = self.selectedDetailIds
                .map((rowIndex) => self.overtimeDetailData[rowIndex]?.attendId)
                .filter((id) => id != null);
            return [...new Set(attendIds)];
        },

        buildListPayload({ offset = 0, limit, userId } = {}) {
            const { company } = rootStore;
            const { startDate, endDate, name, hireType, area } = self.filterParams;
            const resolvedUserId = userId ?? self.filterUserId;

            return {
                companyId: company.id,
                startDt: startDate,
                endDt: endDate,
                searchStr: name || '',
                hireTps: hireType && hireType !== 'ALL' ? [String(hireType)] : [],
                areas: area && area !== 'ALL' ? [Number(area)] : [],
                offset,
                limit: limit ?? self.size,
                ...(resolvedUserId ? { userId: resolvedUserId } : {}),
            };
        },

        async fetchEligibleEmployees({ offset = 0, limit } = {}) {
            try {
                const response = await CompanyService.getEligibleEmployees(
                    self.buildListPayload({ offset, limit }),
                );
                return {
                    data: Array.isArray(response?.data) ? response.data : [],
                    count: Number(response?.count) || 0,
                };
            } catch (error) {
                console.error('Error fetching eligible employees:', error);
                showNotification(t('compensationLeave.tabTarget'), t('common.label.fail'), 'danger');
                return { data: [], count: 0 };
            }
        },

        async fetchOvertimeDetails(userId, { offset = 0, limit } = {}) {
            try {
                const response = await CompanyService.getOvertimeDetails(
                    self.buildListPayload({ offset, limit, userId }),
                );
                return {
                    data: Array.isArray(response?.data) ? response.data : [],
                    count: Number(response?.count) || 0,
                };
            } catch (error) {
                console.error('Error fetching overtime details:', error);
                showNotification(t('compensationLeave.tabOvertimeDetail'), t('common.label.fail'), 'danger');
                return { data: [], count: 0 };
            }
        },

        /**
         * Load remaining pages in background after the first page is shown.
         * Uses offset/limit (offset starts at 0); total comes from response count.
         */
        fetchBackgroundData(listType) {
            const type = listType || (self.isOvertimeDetailTab ? TAB_OVERTIME_DETAIL : TAB_TARGET);
            const totalCount = self.dataCount;
            const limit = self.size;
            const userId = self.filterUserId;

            self.setIsBackgroundLoading(true);
            self.backgroundFetchRunId += 1;
            const backgroundRunId = self.backgroundFetchRunId;

            (async () => {
                try {
                    for (let offset = limit; offset < totalCount; offset += limit) {
                        if (backgroundRunId !== self.backgroundFetchRunId) {
                            break;
                        }

                        try {
                            const result =
                                type === TAB_OVERTIME_DETAIL
                                    ? await self.fetchOvertimeDetails(userId, { offset, limit })
                                    : await self.fetchEligibleEmployees({ offset, limit });

                            if (result.data.length && backgroundRunId === self.backgroundFetchRunId) {
                                if (type === TAB_OVERTIME_DETAIL) {
                                    self.appendOvertimeDetailData(result.data);
                                } else {
                                    self.appendTargetData(result.data);
                                }
                            }
                        } catch (error) {
                            console.error('Error loading background page', offset, error);
                        }
                    }
                } finally {
                    if (backgroundRunId === self.backgroundFetchRunId) {
                        self.setIsBackgroundLoading(false);
                    }
                }
            })();
        },

        async getCommCodes() {
            const { company } = rootStore;
            try {
                const response = await CompanyService.getCommCodeNew({
                    companyId: company.id,
                    commCodeType: 'HIRE_TYPE',
                });

                const hireTypeList =
                    response?.data?.map((v) => ({
                        value: v?.detail_seq,
                        label: getLocalizedField(v, 'comm_code_name'),
                    })) || [];
                self.setHireTypeList(hireTypeList);
                return hireTypeList;
            } catch (error) {
                console.error('Error fetching employee types:', error);
                return [];
            }
        },

        async getWorkPlace() {
            const { company } = rootStore;
            try {
                const response = await CompanyService.getWorkingArea(company.id);
                if (response.data) {
                    const workPlaceList =
                        response?.data?.map((v) => ({
                            value: v?.id,
                            label: v?.name === '' ? '본사' : v?.name,
                        })) || [];
                    self.setWorkPlaceList(workPlaceList);
                    return workPlaceList;
                }
                return [];
            } catch (error) {
                console.error('Error fetching work places:', error);
                return [];
            }
        },

        async getTargetList() {
            self.cancelBackgroundLoading();
            self.setIsLoading(true);
            try {
                const { data, count } = await self.fetchEligibleEmployees({
                    offset: 0,
                    limit: self.size,
                });
                self.setTargetData(data);
                if (!self.isOvertimeDetailTab) {
                    self.setDataCount(count);
                }
                if (count > self.size) {
                    self.fetchBackgroundData(TAB_TARGET);
                }
                return data;
            } catch (error) {
                console.error('Error loading compensation leave list:', error);
                self.setTargetData([]);
                if (!self.isOvertimeDetailTab) {
                    self.setDataCount(0);
                }
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },

        async getOvertimeDetailList(userId) {
            if (userId != null) {
                self.setFilterUserId(Number(userId));
            }
            self.cancelBackgroundLoading();
            self.setIsLoading(true);
            try {
                const { data, count } = await self.fetchOvertimeDetails(userId, {
                    offset: 0,
                    limit: self.size,
                });
                self.setOvertimeDetailData(data);
                self.clearSelectedDetailIds();
                if (self.isOvertimeDetailTab) {
                    self.setDataCount(count);
                }
                if (count > self.size) {
                    self.fetchBackgroundData(TAB_OVERTIME_DETAIL);
                }
                return data;
            } catch (error) {
                console.error('Error loading overtime detail list:', error);
                self.setOvertimeDetailData([]);
                if (self.isOvertimeDetailTab) {
                    self.setDataCount(0);
                }
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },

        async getCurrentList() {
            if (self.isOvertimeDetailTab) {
                return self.getOvertimeDetailList();
            }
            return self.getTargetList();
        },

        async getDataDownload() {
            return self.isOvertimeDetailTab
                ? self.overtimeDetailData.slice()
                : self.targetData.slice();
        },

        async generateCompensatoryLeave() {
            const { company } = rootStore;
            const attendIds = self.getSelectedAttendIds();
            const premiumRate = company?.get?.compensatoryLeaveInfo?.premiumRate ?? {};
            const minimumAccrualUnit =
                company?.get?.compensatoryLeaveInfo?.minimumAccrualUnit?.hours || 0;
            const isUseAllCpLeaveAccrualScope = company?.get?.compensatoryLeaveInfo?.accrualScope === 'ALL' ?? false;

            const selectedRows = self.selectedDetailIds
                .map((rowIndex) => self.overtimeDetailData[rowIndex])
                .filter(Boolean);

            // Sum expected hours per user — each user must meet the minimum on their own
            const expectedHoursByUser = selectedRows.reduce((hoursByUser, item) => {
                const userId = item?.userId;
                if (userId == null) {
                    return hoursByUser;
                }
                const current = hoursByUser.get(userId) || 0;
                hoursByUser.set(
                    userId,
                    current + calculateCompensatoryHours(item, premiumRate, isUseAllCpLeaveAccrualScope),
                );
                return hoursByUser;
            }, new Map());

            const minExpectedHours = expectedHoursByUser.size
                ? Math.min(...expectedHoursByUser.values())
                : 0;

            if (!attendIds.length || minExpectedHours < minimumAccrualUnit) {
                showNotification(
                    t('compensationLeave.generateCompensatoryLeave'),
                    t('compensationLeave.minimumHoursRequiredForCompensatoryLeave', {
                        hours: minimumAccrualUnit,
                    }),
                    'danger',
                );
                return false;
            }

            const listUserIds = selectedRows.map((item) => item?.userId).filter(Boolean);

            self.setIsLoading(true);
            try {
                await CompanyService.generateCompensatoryLeave({
                    attendIds,
                    manual: true,
                    userIds: listUserIds,
                });
                showNotification(
                    t('compensationLeave.generateCompensatoryLeave'),
                    t('compensationLeave.generateCompensatoryLeaveSuccess'),
                    'success',
                );
            } catch (error) {
                console.error('Error generating compensatory leave:', error);
                showNotification(
                    t('compensationLeave.generateCompensatoryLeave'),
                    t('compensationLeave.generateCompensatoryLeaveFailed'),
                    'danger',
                );
                self.setIsLoading(false);
                return false;
            }

            await self.getOvertimeDetailList();
            return true;
        },

        async initialize() {
            await Promise.all([self.getCommCodes(), self.getWorkPlace()]);
            await self.getCurrentList();
        },
    }));
